import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Progress,
} from 'reactstrap'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
import { Image as ImageIcon, Trash, Upload } from 'react-feather'

import ICXAlert from 'assets/icons/ic-x-alert.svg'
import CustomButton from 'components/Buttons/CustomButton'
import PMBColorPicker from 'components/PMBColorPicker'
import {
  setCompanyInfo,
  setCompanyLogo,
} from 'features/private-views/dashboard/dashboardSlice'
import {
  useUpdateCompanyInfoMutation,
  useUpdateCompanyLogoMutation,
} from '../settingApiSlice'
import useGetMyProfileAvaQuery from 'hooks/useGetMyProfileAvaQuery'
import usePartialUpload from 'hooks/usePartialUpload'

const generateSuggestedShortName = (companyName = '') => {
  if (!companyName) return ''

  // Hilangkan kata "PT" atau "PT." (case-insensitive)
  let cleaned = companyName
    .replace(/(?:^|\b)(?:PT|CV)\.?(?:\b|\s+|$)/gi, ' ')
    .trim()

  if (!cleaned) {
    cleaned = companyName.trim()
  }

  const words = cleaned.split(/\s+/).filter(Boolean)

  if (words.length === 0) return ''

  if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase()
  }

  // Ambil huruf pertama dari setiap kata (contoh: "Intan Berlabuh Mutiara" -> "IBM"), maksimal 3 karakter
  return words
    .map((w) => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase()
}

const ModalEditCompanyInfo = ({
  modal,
  toggle,
  ownerData,
  refetchSettings,
  ...args
}) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const profile = useGetMyProfileAvaQuery({ skip: false })
  const { uploadFile } = usePartialUpload()

  const fileInputRef = useRef(null)
  const [dataImg, setDataImg] = useState(null)
  const [previewImage, setPreviewImage] = useState(null)
  const [isDeletingLogo, setIsDeletingLogo] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isPartialUploading, setIsPartialUploading] = useState(false)

  const [name, setName] = useState('')
  const [shortName, setShortName] = useState('')
  const [colorCode, setColorCode] = useState('')
  const [errorMessage, setErrorMessage] = useState(null)
  const [visibleErrorMsg, setVisibleErrorMsg] = useState(false)

  const [updateCompanyLogo, { isLoading: isUpdatingLogo }] =
    useUpdateCompanyLogoMutation()
  const [updateCompanyInfo, { isLoading: isUpdatingInfo }] =
    useUpdateCompanyInfoMutation()

  const isLoading = isUpdatingInfo || isUpdatingLogo || isPartialUploading

  const suggestedShortName = generateSuggestedShortName(ownerData?.companyName)

  useEffect(() => {
    if (modal) {
      const companyNameVal = ownerData?.companyName || ''
      setName(companyNameVal)

      const suggested = generateSuggestedShortName(companyNameVal)
      // Gunakan companyShortName yang tersimpan jika ada, jika belum ada gunakan saran
      setShortName((ownerData?.companyShortName || suggested || '').slice(0, 3))

      let initialColor = ownerData?.companyColorCode || ''
      if (initialColor && !initialColor.startsWith('#')) {
        initialColor = `#${initialColor}`
      }
      setColorCode(initialColor)

      // Reset logo states
      setDataImg(null)
      setPreviewImage(null)
      setIsDeletingLogo(false)
      setUploadProgress(0)
      setIsPartialUploading(false)
      setErrorMessage(null)
      setVisibleErrorMsg(false)
    }
  }, [modal, ownerData])

  const fnCloseErrorMsg = () => {
    setVisibleErrorMsg(false)
    setErrorMessage(null)
  }

  const handleFileChange = (event) => {
    fnCloseErrorMsg()
    const file = event.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
    if (!allowedTypes.includes(file.type)) {
      const msg = 'Hanya file JPG dan PNG yang diperbolehkan.'
      setErrorMessage(msg)
      setVisibleErrorMsg(true)
      enqueueSnackbar(msg, { variant: 'error' })
      return
    }

    const maxSizeMB = 4
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    if (file.size > maxSizeBytes) {
      const msg = `Ukuran file melebihi batas ${maxSizeMB}MB.`
      setErrorMessage(msg)
      setVisibleErrorMsg(true)
      enqueueSnackbar(msg, { variant: 'error' })
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      setPreviewImage(reader.result)
    }
    reader.readAsDataURL(file)
    setDataImg(file)
    setIsDeletingLogo(false)
    event.target.value = null
  }

  const handleRemoveLogo = () => {
    if (dataImg || previewImage) {
      setDataImg(null)
      setPreviewImage(null)
    } else if (ownerData?.companyLogo) {
      setIsDeletingLogo(true)
    }
  }

  const handleUndoRemoveLogo = () => {
    setIsDeletingLogo(false)
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()
    setErrorMessage(null)
    setVisibleErrorMsg(false)

    // Deteksi apakah logo diubah atau dihapus
    const isLogoChanged = Boolean(dataImg || isDeletingLogo)

    // Deteksi apakah singkatan atau kode warna diubah
    const initialShortName = (ownerData?.companyShortName || '').trim().slice(0, 3)
    const currentShortName = (shortName || '').trim().slice(0, 3)
    const isShortNameChanged = currentShortName !== initialShortName

    const initialColor = (ownerData?.companyColorCode || '').replace(/^#/, '').toUpperCase()
    const currentColor = (colorCode || '').replace(/^#/, '').toUpperCase()
    const isColorChanged = currentColor !== initialColor

    const isInfoChanged = isShortNameChanged || isColorChanged

    // Jika tidak ada perubahan sama sekali, tidak perlu panggil API
    if (!isLogoChanged && !isInfoChanged) {
      enqueueSnackbar('Tidak ada perubahan yang disimpan.', { variant: 'info' })
      toggle()
      return
    }

    try {
      // 1. Panggil API Logo HANYA jika ada perubahan logo
      if (isLogoChanged) {
        if (dataImg) {
          setIsPartialUploading(true)
          setUploadProgress(0)

          const uploadResult = await uploadFile(
            dataImg,
            (percentage) => {
              setUploadProgress(percentage)
            },
            true,
          )

          const logoUrl = uploadResult?.url
          if (!logoUrl) {
            const msg =
              uploadResult?.message ||
              uploadResult?.payload ||
              'Logo baru harus gambar yang valid dan diunggah ke PasarMIKRO.'
            setErrorMessage(msg)
            setVisibleErrorMsg(true)
            enqueueSnackbar(msg, { variant: 'error' })
            setIsPartialUploading(false)
            return
          }

          const res = await updateCompanyLogo({
            logo: logoUrl,
          }).unwrap()

          const finalLogo = res?.logo || logoUrl
          dispatch(setCompanyLogo(finalLogo))
          setIsPartialUploading(false)
        } else if (isDeletingLogo) {
          await updateCompanyLogo({
            logo: null,
          }).unwrap()
          dispatch(setCompanyLogo(null))
        }
      }

      // 2. Panggil API Info HANYA jika singkatan atau kode warna berubah
      if (isInfoChanged) {
        const payload = {
          colorCode: currentColor,
          shortName: currentShortName,
        }

        await updateCompanyInfo(payload).unwrap()

        dispatch(
          setCompanyInfo({
            companyShortName: currentShortName,
            companyColorCode: currentColor,
          }),
        )
      }

      if (refetchSettings) refetchSettings()
      if (profile?.refetch) profile.refetch()

      enqueueSnackbar('Profil perusahaan berhasil diperbarui.', {
        variant: 'success',
      })
      setTimeout(toggle, 250)
    } catch (error) {
      setIsPartialUploading(false)
      let msg =
        error?.data?.message ||
        error?.data?.meta?.message ||
        error?.message ||
        'Terjadi kendala saat memperbarui profil perusahaan.'

      if (error?.status === 404) {
        msg =
          'Akun partnership tidak ditemukan. Pastikan akun Anda terdaftar dalam partnership.'
      } else if (error?.status === 406) {
        msg =
          'Anda tidak memiliki akses untuk mengubah informasi perusahaan ini.'
      }

      setErrorMessage(msg)
      setVisibleErrorMsg(true)
      enqueueSnackbar(msg, { variant: 'error' })
    }
  }

  return (
    <Modal
      data-testid="company-info-edit-modal"
      isOpen={modal}
      toggle={toggle}
      {...args}
      style={{ maxWidth: 520 }}
    >
      <ModalBody className="p-4">
        <div className="mb-3">
          <h5 className="text-left fw-bold mb-1">Ubah Profil Perusahaan</h5>
          <p className="text-left text-muted text-sm mb-0">
            Perbarui logo, singkatan, dan kode warna identitas perusahaan Anda.
          </p>
        </div>

        {errorMessage && (
          <Alert
            isOpen={visibleErrorMsg}
            toggle={fnCloseErrorMsg}
            color="danger"
            className="d-flex align-items-center gap-2 my-3"
          >
            <img src={ICXAlert} alt="alert icon" width={18} height={18} />
            <span className="text-sm">{errorMessage}</span>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Logo Perusahaan */}
          <FormGroup className="mb-3">
            <Label className="fw-semibold text-sm mb-1">
              Logo Perusahaan
            </Label>
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '8px',
                  border: isDeletingLogo
                    ? '2px dashed #dc3545'
                    : '1px solid #dee2e6',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDeletingLogo ? '#fff5f5' : '#f8f9fa',
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                {isDeletingLogo ? (
                  <div className="text-center p-1">
                    <Trash size={18} className="text-danger mb-1" />
                    <span
                      style={{
                        fontSize: '9px',
                        display: 'block',
                        color: '#dc3545',
                        fontWeight: 600,
                        lineHeight: 1.1,
                      }}
                    >
                      Akan Dihapus
                    </span>
                  </div>
                ) : previewImage || ownerData?.companyLogo ? (
                  <img
                    src={previewImage || ownerData?.companyLogo}
                    alt="Logo perusahaan"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <div className="text-center text-muted">
                    <ImageIcon size={24} className="text-muted mb-1" />
                    <span style={{ fontSize: '10px', display: 'block' }}>
                      Belum ada
                    </span>
                  </div>
                )}
              </div>

              <div className="d-flex flex-column gap-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={isLoading}
                />
                <div className="d-flex align-items-center gap-2">
                  {isDeletingLogo ? (
                    <CustomButton
                      color="secondary"
                      outline
                      size="sm"
                      type="button"
                      disabled={isLoading}
                      onClick={handleUndoRemoveLogo}
                    >
                      Batal Hapus
                    </CustomButton>
                  ) : (
                    <>
                      <CustomButton
                        color="primary"
                        outline
                        size="sm"
                        type="button"
                        disabled={isLoading}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload size={14} style={{ marginRight: 4 }} />
                        {previewImage || ownerData?.companyLogo
                          ? 'Ganti Logo'
                          : 'Pilih Logo'}
                      </CustomButton>

                      {(previewImage || ownerData?.companyLogo) && (
                        <CustomButton
                          color="danger"
                          outline
                          size="sm"
                          type="button"
                          disabled={isLoading}
                          onClick={handleRemoveLogo}
                        >
                          <Trash size={14} style={{ marginRight: 4 }} />
                          Hapus Logo
                        </CustomButton>
                      )}
                    </>
                  )}
                </div>
                <small className="text-muted text-xs">
                  Format JPG atau PNG, maks. 4MB.
                </small>
              </div>
            </div>

            {isPartialUploading && (
              <div className="mt-2">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-muted text-xs">Mengunggah logo...</small>
                  <small className="text-muted text-xs">{uploadProgress}%</small>
                </div>
                <Progress
                  value={uploadProgress}
                  animated={uploadProgress < 100}
                  style={{ height: '6px' }}
                />
              </div>
            )}
          </FormGroup>

          {/* Nama Perusahaan (Disabled) */}
          <FormGroup className="mb-3">
            <Label for="company-name" className="fw-semibold text-sm mb-1">
              Nama Perusahaan
            </Label>
            <Input
              id="company-name"
              type="text"
              value={name}
              disabled
              readOnly
              className="bg-light text-muted"
              placeholder="Nama perusahaan"
            />
            <small className="text-muted text-xs mt-1 d-block">
              *Nama perusahaan tidak dapat diubah saat ini.
            </small>
          </FormGroup>

          {/* Singkatan (Short Name) */}
          <FormGroup className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <Label for="company-short-name" className="fw-semibold text-sm mb-0">
                Singkatan
              </Label>
              <span className="text-muted text-xs">Maksimal 3 karakter</span>
            </div>
            <Input
              id="company-short-name"
              type="text"
              value={shortName}
              onChange={(e) => setShortName(e.target.value.slice(0, 3))}
              placeholder="Contoh: PM"
              maxLength={3}
              disabled={isLoading}
            />
            <div className="d-flex justify-content-between align-items-center mt-1">
              {suggestedShortName ? (
                <div className="d-flex align-items-center gap-1">
                  <span className="text-muted text-xs">Saran:</span>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none fw-semibold text-primary text-xs"
                    onClick={() => setShortName(suggestedShortName)}
                    title="Gunakan saran ini"
                  >
                    {suggestedShortName}
                  </button>
                  {shortName !== suggestedShortName && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="text-muted text-xs ms-1"
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => setShortName(suggestedShortName)}
                    >
                      (Klik untuk gunakan)
                    </span>
                  )}
                </div>
              ) : (
                <div />
              )}
              <span className="text-muted text-xs">
                {shortName?.length || 0}/3
              </span>
            </div>
          </FormGroup>

          {/* Kode Warna */}
          <FormGroup className="mb-4">
            <Label for="company-color-code" className="fw-semibold text-sm mb-1">
              Kode Warna Perusahaan
            </Label>
            <PMBColorPicker
              id="company-color-code"
              value={colorCode}
              onChange={(newColor) => setColorCode(newColor)}
              disabled={isLoading}
              placeholder="Contoh: 006386 atau 00638680"
            />
            <small className="text-muted text-xs mt-1 d-block">
              Klik kotak warna untuk memilih warna dan transparansi, atau ketik langsung kode hex.
            </small>
          </FormGroup>

          <div className="d-flex w-100 gap-2 justify-content-end pt-2">
            <CustomButton
              color="secondary"
              outline
              type="button"
              className="text-sm px-4"
              testId="company-info-cancel-btn"
              disabled={isLoading}
              onClick={toggle}
            >
              Batal
            </CustomButton>
            <CustomButton
              color="primary"
              type="submit"
              className="text-sm px-4"
              testId="company-info-save-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Menyimpan...' : 'Simpan Perubahan'}
            </CustomButton>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

export default ModalEditCompanyInfo
